import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Globe, X, Save } from "lucide-react";

/**
 * TranslationsEditor — modal that lets an admin edit `translations.ml` and `translations.hi`
 * on a given document by calling PATCH /api/translations/{collection}/{id}.
 *
 * Props:
 *   open      — boolean
 *   onClose   — () => void
 *   collection— "board-members" | "jobs" | "initiatives"
 *   doc       — the full document (must include id and its base fields)
 *   fields    — array of field names to translate, e.g. ["title","affiliation"]
 *   onSaved   — (updatedDoc) => void
 */
export default function TranslationsEditor({ open, onClose, collection, doc, fields, onSaved }) {
  const [lang, setLang] = useState("ml");
  const initial = (doc?.translations && doc.translations[lang]) || {};
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Reset when lang or doc changes
  const switchLang = (l) => {
    setLang(l);
    setValues((doc?.translations && doc.translations[l]) || {});
  };

  if (!open || !doc) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/translations/${collection}/${doc.id}`, {
        lang,
        fields: values,
      });
      toast.success(`Saved ${lang.toUpperCase()} translation`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-surface border border-ink/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="translations-editor">
        <div className="flex items-start justify-between p-5 border-b border-ink/10">
          <div>
            <div className="tag mb-1 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" aria-hidden="true" /> // translations</div>
            <h2 className="font-display font-bold text-ink text-lg">{doc.name || doc.title || doc.slug}</h2>
          </div>
          <button data-testid="tr-close" onClick={onClose} aria-label="Close" className="text-ink/60 hover:text-ink">
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="p-5 border-b border-ink/10 flex gap-2">
          {["ml", "hi"].map((l) => (
            <button
              key={l}
              data-testid={`tr-lang-${l}`}
              onClick={() => switchLang(l)}
              className={`px-4 py-1.5 text-sm border ${lang === l ? "bg-brand border-teal text-ink" : "border-ink/15 text-ink/70 hover:border-ink/40"}`}
            >
              {l === "ml" ? "മലയാളം" : "हिन्दी"}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {fields.map((f) => (
            <label key={f} className="block">
              <div className="text-xs font-mono uppercase tracking-widest text-ink/50 mb-1 flex items-center justify-between">
                <span>{f}</span>
                <span className="text-ink/30">EN: {String(doc[f] || "").slice(0, 80)}</span>
              </div>
              <textarea
                data-testid={`tr-field-${f}`}
                rows={f.includes("description") || f.includes("summary") || f.includes("body") ? 4 : 2}
                value={values[f] || ""}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-3 py-2 text-ink text-sm resize-none"
              />
            </label>
          ))}
        </div>

        <div className="p-5 border-t border-ink/10">
          <button
            data-testid="tr-save"
            onClick={save}
            disabled={saving}
            className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-ink py-2.5 flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" aria-hidden="true" /> {saving ? "Saving…" : `Save ${lang.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}
